import { Logger, namespace } from '../../../../../FrontendBundle/Resources/js/ofcs';
import ItemCollection from '../item/itemCollection';

namespace('Ofcs.Editor.Canvas');

/**
 * @extends ItemCollection
 */
export default window.Ofcs.Editor.Canvas.SelectionCollection = ItemCollection.extend({

    /**
     * Triggered if the selection has changed
     *
     * @event selectionchange
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection Selection
     */

    /**
     * Triggered if the selection has changed
     *
     * @event select
     * @param {Ofcs.Editor.Item.ItemInterface} item The selected item
     */

    /**
     * Triggered if the selection has changed
     *
     * @event deselect
     * @param {Ofcs.Editor.Item.ItemInterface} item The deselected item
     */

    /**
     * Triggered every time a click occurs within the active icon
     *
     * @event selectClick
     */

    /**
     * @constructor
     */
    initialize() {
        this.on('add', (item) => {
            item.trigger('select', item);
            item.on('delete', () => this.deselectItem(item), this);
        }, this);

        this.on('remove', (item) => {
            item.trigger('deselect', item);
            item.off('delete', null, this);
        }, this);

        this.selectionHasChanged = false;
    },

    /**
     * Remove all items from selection
     */
    clear() {
        Logger.debug('selection.clear()');
        const removeItems = [];

        this.each(selectedItem => removeItems.push(selectedItem));

        this.remove(removeItems);

        if (removeItems.length) {
            this.trigger('selectionchange', this);
        }
    },

    /**
     * Select a single item, optionally clearing the current selection. Fires the #selectionchange event.
     *
     * @param {ItemInterface} item Item
     * @param {boolean} clearSelection Clear existing selection?
     * @param {Object} triggerParameters Parameters for trigger
     */
    selectItem(item, clearSelection, triggerParameters) {
        Logger.debug('selection.selectItem()', item);
        this.selectItems([item], clearSelection, triggerParameters);
        item.trigger('selectClick');
    },

    /**
     * Select an array of items optionally clearing the current selection.
     *
     * @param {ItemInterface[]} items Items to select
     * @param {boolean} clearSelection Clear existing selection?
     */
    markItems(items, clearSelection) {
        Logger.debug('selection.markItems()', items);

        let selectionChanged = false;
        const removeItems = [];

        this.each((selectedItem) => {
            if (clearSelection && items.indexOf(items, selectedItem) === -1) {
                removeItems.push(selectedItem);
            }
        }, this);

        if (removeItems.length > 0) {
            selectionChanged = true;
            this.remove(removeItems);
        }

        for (let i = 0; i < items.length; i += 1) {
            const item = items[i];
            const alreadySelected = this.get(item.cid);

            if (item instanceof Ofcs.Editor.Item.AbstractItem) {
                const parent = item.getParent();
                if (parent instanceof Ofcs.Editor.Item.AbstractItem) {
                    items.push(parent);
                }
            }

            if (!alreadySelected) {
                selectionChanged = true;
                this.add(item);
            }
        }

        this.selectionHasChanged = selectionChanged || this.selectionHasChanged;
    },

    /**
     * Select an array of items optionally clearing the current selection. Fires the #selectionchange event.
     *
     * @param {ItemInterface[]} items Items to select
     * @param {boolean} clearSelection Clear existing selection?
     * @param {Object} triggerParameters Parameters for trigger
     */
    selectItems(items, clearSelection, triggerParameters) {
        Logger.debug('selection.selectItems()', items);
        this.markItems(items, clearSelection);
        this.selectMarkedItems(triggerParameters);
    },

    /**
     * @param {Object} triggerParameters Parameters for trigger
     */
    selectMarkedItems(triggerParameters) {
        if (this.selectionHasChanged) {
            this.trigger('selectionchange', this, triggerParameters);
            this.selectionHasChanged = false;
        }
    },

    /**
     * Toggle a single item. Fires the #selectionchange event.
     *
     * @param {ItemInterface} item Item
     */
    toggleItem(item) {
        Logger.debug('Selection.toggleItem()', item, this);

        const alreadySelected = this.get(item.cid);

        if (alreadySelected) {
            this.remove(item);
        } else {
            this.add(item);
        }

        this.trigger('selectionchange', this);
    },

    /**
     * Deselect a single item.
     *
     * @param {ItemInterface} item Item
     */
    deselectItem(item) {
        Logger.debug('selection.deselectItem()', item);

        const alreadySelected = this.get(item.cid);

        if (!alreadySelected) {
            return;
        }

        this.remove(item);
        this.trigger('selectionchange', this);
    },

    /**
     * Move all selected items.
     *
     * @param {Distance} distance
     */
    moveItems(distance) {
        this.each(item => item.move(distance));
    },
});
